import qs from 'qs';
import { FILTERS, VERSION, VERSION_PARAM, MIN_ZOOM, MAX_ZOOM } from '../constants';

/**
 * Converts an object with the format:
 * {
 *  northeast: { lat: ..., lng: ... },
 *  southwest: { lat: ..., lng: ... },
 * }
 *
 * to a string with the format:
 * 'northeastLat,northeastLng,southwestLat,southwestLng'
 *
 * @param {Object} bounds
 * @return {String} - Bounds string.
 */
const boundsToString = bounds => {
  const { northeast, southwest } = bounds;
  return `${northeast.lat},${northeast.lng},${southwest.lat},${southwest.lng}`;
};

/**
 * Takes a bounds string and converts it to bounds. A bounds string takes the format:
 * 'northeastLat,northeastLng,southwestLat,southwestLng'
 *
 * @param {String} string - Bounds string to convert to bounds.
 * @return {Object} - Bounds object, with northeast and southwest children.
 */
const stringToBounds = string => {
  let bounds = null;

  try {
    const coords = string
      .split(',')
      .slice(0, 4)
      .map(c => parseFloat(c));

    if (coords.length === 4 && coords.every(c => c)) {
      bounds = {
        northeast: {
          lat: coords[0],
          lng: coords[1],
        },
        southwest: {
          lat: coords[2],
          lng: coords[3],
        },
      };
    }
  } catch (e) {
    // Unable to parse bounds.
  }

  return bounds;
};

/**
 * Replaces the provided parameter name with the provided value. If the provided parameter value is
 * a falsy, then the query parameter is removed from the query string.
 *
 * @param {String} name - Query parameter name.
 * @param {String} value - New query parameter value.
 * @param {String} url - Current url.
 */
const replaceQueryParam = (name = '', value = '', url = '') => {
  // Get the part of the url before the query string (path).
  let path = url;
  let query = '';

  if (url.indexOf('?') > -1) {
    const parsed = url.split('?');

    // eslint-disable-next-line prefer-destructuring
    path = parsed[0];
    // eslint-disable-next-line prefer-destructuring
    query = parsed[1];
  }

  const params = qs.parse(query);

  if (value) {
    params[name] = value;
  } else {
    delete params[name];
  }

  return `${path}?${qs.stringify(params)}`;
};

/**
 * Gets a query parameter from the url.
 *
 * @param {String} name - Parameter name.
 * @param {String} url - Url.
 * @return {String} - Query parameter value.
 */
const getQueryParam = (name, url) => {
  const filteredName = name.replace(/[[\]]/g, '\\$&');
  const regex = new RegExp(`[?&]${filteredName}(=([^&#]*)|&|#|$)`);
  const results = regex.exec(url);

  if (!results || !results[2]) return '';

  return decodeURIComponent(results[2].replace(/\+/g, ' '));
};

/**
 * Parses the provided filters and only adds filters that are allowed.
 *
 * @param {String} filters - Filters query param string.
 * @return {Array<String>} - Array of valid filters or null if there are no valid filters.
 */
const parseFilters = filters => {
  if (filters && filters.length > 0) {
    return filters.split(',').filter(filter => FILTERS.indexOf(filter) > -1);
  }

  return [];
};

/**
 * Parses the provided zoom parameters and only returns a valid zoom.
 *
 * @param {String} zoom - Zoom query param string.
 * @return {Number} - Parsed zoom or null if invalid.
 */
const parseZoom = zoom => {
  const intValue = parseInt(zoom, 10);

  if (intValue && intValue >= MIN_ZOOM && intValue <= MAX_ZOOM) return intValue;
  return null;
};

/**
 * Initializes the provided url and cleans it from hash and query params.
 *
 * @param {Object} location - Window location object.
 * @return {String} - Cleaned url.
 */
export const initUrl = location => {
  const { protocol, host, pathname, href } = location;

  let updatedUrl = `${protocol}//${host}${pathname}?${VERSION_PARAM}=${VERSION}`;

  // Check if there is a wcmmode=disabled parameter. If so include it (for AEM).
  const wcmmode = getQueryParam('wcmmode', href);
  const kiosk = getQueryParam('platform', href);
  if (kiosk) updatedUrl = `${updatedUrl}&platform=kiosk`;
  if (wcmmode) updatedUrl = `${updatedUrl}&wcmmode=${wcmmode}`;

  // Make sure we retain redirect=no in the URL
  const redirect = getQueryParam('redirect', href);
  if (redirect) updatedUrl = `${updatedUrl}&redirect=${redirect}`;

  return updatedUrl;
};

/**
 * Replace the current url with the provided url string using history api.
 *
 * @param {String} url
 */
export const updateUrl = url => {
  if (window.location.href.includes('platform=kiosk')) {
    const isCef = navigator.vendor === 'Google Inc.';
    const isEdge = /Edge/.test(navigator.userAgent);
    const webViewANEWrapper = {
      postMessage: message => {
        if (isCef && typeof window.webViewANE !== 'undefined') {
          if (typeof window.webViewANE.postMessage !== 'undefined') {
            window.webViewANE.postMessage(message);
          }
        } else if (isEdge && typeof window.external !== 'undefined') {
          window.external.notify(JSON.stringify(message));
        } else if (typeof window.webkit !== 'undefined') {
          window.webkit.messageHandlers.webViewANE.postMessage(message);
        }
      },
    };

    const getWebviewANE = () => {
      return webViewANEWrapper;
    };

    getWebviewANE().postMessage({
      functionName: 'js_to_as',
      args: ['reset_activity_timer'],
    });

    if (isCef) {
      // Important: CefSharp binding must now be performed async
      if (typeof webViewANE === 'undefined') {
        window.CefSharp.BindObjectAsync('webViewANE').then(function() {
          window.webViewANE.postMessage({ functionName: 'js_to_as' }).then(function() {});
        });
      }
    } else {
      const messageToPost = { functionName: 'js_to_as' };
      getWebviewANE().postMessage(messageToPost);
    }
  }
  window.history.replaceState(null, null, url);
};

/**
 * Updates bounds query parameter in the url.
 *
 * @param {Object} bounds - Bounds object with northeast and southwest properties.
 * @return {String} - Updated url.
 */
export const updateUrlBounds = (bounds, url) => {
  const boundsString = boundsToString(bounds);
  return replaceQueryParam('bounds', boundsString, url);
};

/**
 * Updates zoom query parameter in the url.
 *
 * @param {String} zoom - Zoom to update url to.
 * @return {String} - Updated url.
 */
export const updateUrlZoom = (zoom, url) => replaceQueryParam('zoom', zoom, url);

/**
 * Updates platform query parameter in the url.
 *
 * @param {String} platform - Platform to update url to.
 * @return {String} - Updated url.
 */
export const updateUrlPlatform = (platForm, url) => replaceQueryParam('platform', platForm, url);

/**
 * Update filter query parameter in the url.
 *
 * @param {Array<String>} filters - Filters to update the url with.
 * @return {String} - Updated url.
 */
export const updateUrlFilters = (filters, url) => {
  const filtersString = filters.join(',');
  return replaceQueryParam('filters', filtersString, url);
};

/**
 * Update location query parameter in the url.
 *
 * @param {Array<String>} filters - Filters to update the url with.
 * @return {String} - Updated url.
 */
export const updateUrlLocation = (location, url) => replaceQueryParam('location', location, url);

/**
 * Update search query parameter in the url.
 *
 * @param {String} search - Search input to update the url with.
 * @return {String} - Updated url.
 */
export const updateUrlSearch = (search, url) => replaceQueryParam('search', search, url);

/**
 * Parses a findus url following the legacy format.
 *
 * In the legacy format, the location coordinates are defined in the hash and filters are defined
 * in the "search" query parameter. A specific place id can be indicated with the "place" query
 * parameter. The default value of the search input can indicated with the "name" query parameter.
 *
 * Ex:
 * https://www.tesla.com/findus#/bounds/37.4695381,-121.589154,37.124493,-122.0456719,d?search=store
 *
 * @param {String} url - URL to parse.
 * @return {Object} - An object with the parsed filter and bounds.
 */
const parseLegacyUrl = url => {
  let bounds = null;

  // Get bounds from hash.
  const boundsIdentifier = '#/bounds/';
  const startOfHash = url.indexOf(boundsIdentifier);

  if (startOfHash > -1) {
    let endOfHash = url.indexOf('?');
    endOfHash = endOfHash > -1 ? endOfHash : url.length;

    const boundsString = url.slice(startOfHash + boundsIdentifier.length, endOfHash);
    bounds = stringToBounds(boundsString);
  }

  return {
    bounds,
    filters: parseFilters(getQueryParam('search', window.location.href)),
    place: getQueryParam('place', window.location.href),
    search: getQueryParam('name', window.location.href),
  };
};

/**
 * Parses a findus url following the new format.
 *
 * In the new format, only query parameters are used (no hash). The following query parameters
 * are utilized:
 *
 * bounds - default bounds for the map
 * filters - default filters for the map
 * place - default selected location for the map
 * search - default search term for the search input
 *
 * Ex:
 * https://www.tesla.com/findus?bounds=37.4695381,-121.589154,37.124493,-122.0456719&filters=store
 *
 * @param {String} url - URL to parse.
 * @return {Object} - An object with the parsed filter and bounds.
 */
const parseNewUrl = url => {
  return {
    bounds: stringToBounds(getQueryParam('bounds', url)),
    filters: parseFilters(getQueryParam('filters', url)),
    place: getQueryParam('location', url) || getQueryParam('place', url),
    search: getQueryParam('search', url),
    zoom: parseZoom(getQueryParam('zoom', url)),
    platform: getQueryParam('platform', url),
  };
};

/**
 * Retrieves bounds and filters from the provided url. Checks to identify if the url format is
 * the old or new format.
 *
 * https://www.tesla.com?bounds=37.51487,-121.04378,36.87657,-122.38068&filters=service,store
 * https://www.tesla.com?place=sandiegosupercharger
 * https://www.tesla.com?search=fremont,california
 *
 * @param {String} url - Url to parse.
 * @param {Boolean} isV2 - Is Find US v2 enabled in the detected country.
 * @return {Object} - Parsed bounds and filters.
 */
export const parseUrl = (url, isV2 = true) => {
  // Check the version of findus. If the newer version, utilize the new url formatting, else
  // check for the legacy formatting.
  //
  // TODO: Once the previous version is fully retired we can remove the check for findus version.
  if (isV2) {
    return parseNewUrl(url);
  }

  // Get bounds and filters from legacy format if possible.
  return parseLegacyUrl(url);
};

/**
 * @description Append URI encoded string of trip origin data
 *
 * @param {Object} path - base path
 * @param {Object} searchedLocation - detail about the searched location to be
 * trip planner origin location field.
 *
 * @return {string}
 */
export function buildTripPlannerLink(path, searchedLocation) {
  const searchQuery = `?origin=${encodeURI(searchedLocation.address)}`;

  return `${path}${searchQuery}`;
}
